import { useEffect, useMemo, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { Card } from '@heroui/react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { initDataUser, useSignal } from '@telegram-apps/sdk-react';
import { useLearningLanguage, type LearningLanguage } from '@/entities/user';
import { ROUTES } from '@/shared/config';
import { delay } from '@/shared/lib/animation';
import { haptics } from '@/shared/lib/telegram/haptics';
import botAvatar from '@/shared/assets/logo.png';

// Небесно-голубой градиент (с 2026-10-09; до этого был сиреневый
// `#dbc1e7 → #f3e9f7 → #fff`). Ответ пользователя внизу лежит на почти белом
// конце градиента — фон приложения (`--color-bg`) там сливался, поэтому у
// пузыря свой голубой тон, плотнее середины градиента.
const CARD_BACKGROUND = 'linear-gradient(160deg, #d9effd 0%, #edf6fd 55%, #ffffff 100%)';
const REPLY_BUBBLE_BACKGROUND = '#e3f0fa';
const DOTS = [0, 1, 2];

/**
 * Приветствие и примеры того, что можно ответить, — витрина, не реальный
 * диалог (тот начинается только после клика). Нарочно на изучаемом языке, а
 * не на языке интерфейса: это демонстрация самой практики, не UI-текст, и
 * показывать её по-русски вводило бы в заблуждение насчёт того, на каком
 * языке идёт разговор. Поэтому и меняется вместе с языком в шапке главной.
 */
const TEASERS: Record<LearningLanguage, { greeting: (name?: string) => string; replies: readonly string[] }> = {
  en: {
    greeting: (name) => (name ? `Hi ${name}! What do you want to discuss?` : 'Hi! What do you want to discuss?'),
    replies: [
      "I'd like to talk about my relationships",
      "Let's talk about my new job",
      'Can we discuss my trip to Italy?',
      'Something about my hobbies, maybe?',
    ],
  },
  de: {
    greeting: (name) => (name ? `Hallo ${name}! Worüber möchtest du reden?` : 'Hallo! Worüber möchtest du reden?'),
    replies: [
      'Ich möchte über meine Beziehung reden',
      'Lass uns über meinen neuen Job sprechen',
      'Können wir über meine Reise nach Italien reden?',
      'Vielleicht über meine Hobbys?',
    ],
  },
  pt: {
    greeting: (name) => (name ? `Oi, ${name}! Sobre o que você quer conversar?` : 'Oi! Sobre o que você quer conversar?'),
    replies: [
      'Quero falar sobre os meus relacionamentos',
      'Vamos falar do meu novo emprego',
      'Podemos conversar sobre a minha viagem para a Itália?',
      'Talvez sobre os meus hobbies?',
    ],
  },
};

const TYPE_MS = 42;
const DELETE_MS = 24;
const INITIAL_DELAY_MS = 500;
const PAUSE_AFTER_GREETING_MS = 500;
const PAUSE_AFTER_TYPE_MS = 1700;
const PAUSE_AFTER_DELETE_MS = 350;

/**
 * Печатает приветствие посимвольно один раз, затем бесконечно печатает и
 * стирает по кругу примеры ответов из `replies` — та же идея «живой ленты»,
 * что маркиз тем в `topic-picker-entry`, только для чат-пузырей.
 *
 * Пишет прямо в DOM (`textContent` и `hidden` у точек и каретки), а не в
 * state: через state это были ~13 коммитов в секунду, бесконечно, и каждый
 * перерисовывал карточку вместе с `Card` HeroUI и аватаром — главный поток не
 * простаивал ни секунды, пока открыта главная. Разметку строк см.
 * `TeaserLine`, React её после маунта не трогает.
 *
 * Пауза, пока карточку не видно: уехала на второй слайд карусели (вьюпорт
 * карусели с `overflow-hidden`, IntersectionObserver это учитывает), за экран
 * или вкладка скрыта. Пропущенный шаг ставится заново с полной задержкой —
 * на глаз это просто чуть более долгая пауза.
 */
function useChatTeaser(greeting: string, replies: readonly string[]) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || replies.length === 0) return;
    const greetingLine = teaserLine(root, 'greeting');
    const replyLine = teaserLine(root, 'reply');
    if (!greetingLine || !replyLine) return;

    let timeout: ReturnType<typeof setTimeout> | undefined;
    let pending: { step: () => void; ms: number } | null = null;
    let visible = false;
    let replyIndex = 0;

    const schedule = (step: () => void, ms: number) => {
      pending = { step, ms };
      if (visible && document.visibilityState === 'visible') run();
    };
    const run = () => {
      if (!pending || timeout !== undefined) return;
      const { step, ms } = pending;
      timeout = setTimeout(() => {
        timeout = undefined;
        pending = null;
        step();
      }, ms);
    };
    const pause = () => {
      clearTimeout(timeout);
      timeout = undefined;
    };
    const updateRunning = () => {
      if (visible && document.visibilityState === 'visible') run();
      else pause();
    };

    const typeReply = (charIndex: number) => {
      const word = replies[replyIndex % replies.length]!;
      replyLine.write(word.slice(0, charIndex), true);
      if (charIndex < word.length) schedule(() => typeReply(charIndex + 1), TYPE_MS);
      else schedule(() => deleteReply(word.length - 1), PAUSE_AFTER_TYPE_MS);
    };

    const deleteReply = (charIndex: number) => {
      const word = replies[replyIndex % replies.length]!;
      replyLine.write(word.slice(0, charIndex), true);
      if (charIndex > 0) {
        schedule(() => deleteReply(charIndex - 1), DELETE_MS);
      } else {
        replyIndex++;
        schedule(() => typeReply(1), PAUSE_AFTER_DELETE_MS);
      }
    };

    const typeGreeting = (charIndex: number) => {
      const done = charIndex >= greeting.length;
      greetingLine.write(greeting.slice(0, charIndex), !done);
      if (!done) schedule(() => typeGreeting(charIndex + 1), TYPE_MS);
      else schedule(() => typeReply(1), PAUSE_AFTER_GREETING_MS);
    };

    greetingLine.write('', false);
    replyLine.write('', false);
    schedule(() => typeGreeting(1), INITIAL_DELAY_MS);

    const observer = new IntersectionObserver(([entry]) => {
      visible = entry?.isIntersecting ?? false;
      updateRunning();
    });
    observer.observe(root);
    document.addEventListener('visibilitychange', updateRunning);

    return () => {
      pause();
      observer.disconnect();
      document.removeEventListener('visibilitychange', updateRunning);
    };
  }, [greeting, replies]);

  return rootRef;
}

type TeaserSlot = 'greeting' | 'reply';

/** Узлы одной строки тизера: пока текста нет — точки «печатает…», с текстом — текст и каретка. */
function teaserLine(root: HTMLElement, slot: TeaserSlot) {
  const line = root.querySelector(`[data-teaser="${slot}"]`);
  const dots = line?.querySelector<HTMLElement>('[data-teaser-dots]');
  const text = line?.querySelector<HTMLElement>('[data-teaser-text]');
  const caret = line?.querySelector<HTMLElement>('[data-teaser-caret]');
  if (!dots || !text || !caret) return null;
  return {
    write(value: string, withCaret: boolean) {
      text.textContent = value;
      dots.hidden = value !== '';
      caret.hidden = value === '' || !withCaret;
    },
  };
}

/** Разметка строки под `teaserLine`: содержимое меняет только `useChatTeaser`, напрямую в DOM. */
function TeaserLine({ slot, className }: { slot: TeaserSlot; className: string }) {
  return (
    <p data-teaser={slot} className={className}>
      <span data-teaser-dots>
        <TypingDots />
      </span>
      <span data-teaser-text />
      <span data-teaser-caret hidden className="anim-caret text-ink-muted">
        |
      </span>
    </p>
  );
}

/** Три скачущих точки — тот же приём, что «печатает…» у тьютора в чате (ChatScreen.ThinkingBubble). */
function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1 py-1" aria-hidden="true">
      {DOTS.map((index) => (
        <span key={index} className="anim-dot size-1.5 rounded-full bg-ink-muted" style={delay(index * 160)} />
      ))}
    </span>
  );
}

/** Промо-карточка свободной практики на главном экране: переход в чат с ИИ-собеседником. */
export function FreePracticeCard() {
  const { t } = useTranslation('FreePracticeCard');
  const navigate = useNavigate();
  const telegramUser = useSignal(initDataUser);
  const firstName = telegramUser?.first_name;
  const teaser = TEASERS[useLearningLanguage().language ?? 'en'];

  const greeting = useMemo(() => teaser.greeting(firstName), [teaser, firstName]);

  const teaserRef = useChatTeaser(greeting, teaser.replies);

  return (
    <button
      type="button"
      className="block w-full text-left transition-transform active:scale-[0.97]"
      onClick={() => {
        haptics.advance();
        void navigate(ROUTES.tutorSelect);
      }}
    >
      <Card className="flex flex-col gap-4 overflow-hidden p-4" style={{ background: CARD_BACKGROUND }}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-0.5">
            <p className="text-[15px] font-semibold leading-snug text-ink">{t('title')}</p>
            <p className="text-xs leading-snug text-ink-muted">{t('subtitle')}</p>
          </div>
          {/* Декоративная стрелка-аффорданс, не своя кнопка — клик уже висит
              на всей карточке (`button` снаружи), вложенный интерактивный
              элемент внутри него был бы невалидным HTML. */}
          <span
            className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white shadow-lg"
            aria-hidden="true"
          >
            <ArrowRight className="size-4 text-ink" />
          </span>
        </div>

        {/* Чатик-тизер: приветствие печатается один раз, ответ ниже печатается
            и стирается по кругу через примеры тем — превью того, как
            начинается настоящий разговор после клика. Аватарки по бокам и
            точки «печатает…», пока зелёный пузырь ещё без текста, — чтобы
            пауза между репликами не читалась как пустое место. `min-h` —
            чтобы карточка была той же высоты, что и соседняя (рулетка тем):
            там высоту держат три ряда бегущей ленты, здесь всего два пузыря,
            без подпорки контент был заметно ниже. `justify-start`, не
            `-center`: при центрировании реплика репетитора прыгала вверх-вниз
            каждый раз, когда ответ снизу перескакивал на вторую строку —
            рост высоты добавляется снизу, а не раздвигает контент от центра. */}
        <div ref={teaserRef} className="flex min-h-[130px] flex-col justify-start gap-3">
          {/* `items-start`, не `-end`: аватарка держится за верх своей строки —
              тот не сдвигается независимо от того, сколько строк займёт
              текст. С `items-end` аватарка была подвешена к нижнему краю
              пузыря и прыгала вверх-вниз при переходе на вторую строку. */}
          <div className="flex items-start gap-2">
            <img
              src={botAvatar}
              alt=""
              aria-hidden="true"
              className="size-7 shrink-0 rounded-full border-2 border-white object-cover shadow-sm"
            />
            {/* Место под пузырь держит невидимый дубль ПОЛНОГО текста
                приветствия (то же `greeting`, не бегущий текст),
                наложенный тем же grid-cell'ом — пузырь сразу занимает
                итоговую высоту (1 или 2 строки, какая нужна конкретному
                имени), и печать до конца строки её не меняет. Без этого
                пришлось бы либо резервировать 2 строки всегда (пустое место
                под короткое приветствие), либо ловить скачок в момент
                переноса — оба варианта хуже. */}
            <div className="grid max-w-[75%] rounded-2xl rounded-tl-md bg-white px-3.5 py-2.5 shadow-sm">
              <p aria-hidden="true" className="invisible [grid-area:1/1] text-sm leading-snug font-medium">
                {greeting}
              </p>
              <TeaserLine slot="greeting" className="[grid-area:1/1] text-sm leading-snug font-medium text-ink" />
            </div>
          </div>
          <div className="flex items-start justify-end gap-2">
            <div
              className="max-w-[75%] rounded-2xl rounded-tr-md px-3.5 py-2.5 shadow-sm"
              style={{ background: REPLY_BUBBLE_BACKGROUND }}
            >
              <TeaserLine slot="reply" className="min-h-[1.2em] text-sm leading-snug font-medium text-ink" />
            </div>
            <UserAvatar photoUrl={telegramUser?.photo_url} name={firstName} />
          </div>
        </div>
      </Card>
    </button>
  );
}

function UserAvatar({ photoUrl, name }: { photoUrl?: string; name?: string }) {
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        aria-hidden="true"
        className="size-7 shrink-0 rounded-full border-2 border-white object-cover shadow-sm"
      />
    );
  }

  return (
    <div
      className="flex size-7 shrink-0 items-center justify-center rounded-full border-2 border-white bg-glass-2 text-xs font-bold text-ink-muted shadow-sm"
      aria-hidden="true"
    >
      {name?.trim().charAt(0).toUpperCase() || '🙂'}
    </div>
  );
}
